import { PaddleInitResponse } from '../../cart/models/paddle-init-response.model';
import { RestoreCart } from '../../cart/models/restore-cart.model';
import { CartSaved } from '../../cart/models/cart-saved.model';
import { AuthService } from '../../auth/services/auth.service';
import { SubscriptionDetail } from '../models/subscriptions.model';
import { CartItem } from '../models/cart/cart-item.model';
import { Injectable, Injector } from '@angular/core';
import {
  BehaviorSubject, Observable, Subject, Subscriber,
} from 'rxjs';
import { Track } from 'src/app/shared/models/tracks-sfx/track.model';
import { Price } from 'src/app/shared/models/price.model';
import { HttpClient } from '@angular/common/http';
import { Service } from 'src/app/shared/services/service';
import { ProcessResponse, ProcessRequest } from '../../cart/models/process.model';
import { first, map } from 'rxjs/operators';
import { DiscountResponse } from '../../cart/models/discount.model';
import { GtagService } from 'src/app/gtag/gtag.service';
import { ReferralService } from 'src/app/shared/services/referral.service';
import { MixedModalComponent } from '../components/cart/mixed-modal/mixed-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of } from "rxjs";


@Injectable({
  providedIn: 'root',
})
export class CartService extends Service {

  public cart: BehaviorSubject<Array<CartItem>>;
  public addedToCart: Subject<CartItem> = new Subject<CartItem>();

  private lastSentError: string;

  constructor(
    http: HttpClient,
    private gtag: GtagService,
    auth: AuthService,
    private referral: ReferralService,
    private modalService: BsModalService,
  ) {
    super(http);

    this.maybeClear();

    if (localStorage.getItem('cart')) {
      this.cart = new BehaviorSubject<Array<CartItem>>(JSON.parse(localStorage.getItem('cart')));
    } else {
      this.cart = new BehaviorSubject<Array<CartItem>>(new Array());
    }
  }

  /**
   * Add item to the cart and stores the new cart in localStorage
   * to keep the cart available in between page reloads.
   */
  add(item: CartItem, fromCart = false) {
    this.cart.value.push(item);
    this.cart.next(this.cart.value);
    localStorage.setItem('cart', JSON.stringify(this.cart.value));
    this.gtag.addItemToCart(item);
    if (!fromCart) {
      this.addedToCart.next(item);
    }
  }

  setCart(items: CartItem[], fromCart = false) {
    this.clear();
    for (const item of items) {
      this.add(item);
    }
  }

  /**
   * Create a new cart item. Called from track details when a track license is added to the cart.
   */
  addTrack(track: Track, price: Price): Observable<void> {
    return new Observable<void>((subscriber) => {
      if (this.hasSubscription()) {
        this.openMixedCartTrackModal(track, price, subscriber);
      } else {
        const item: CartItem =
          {
            name: track.title,
            id: track.id,
            price: price.price,
            salePrice: price.salePrice.toString(),
            regularPrice: price.regularPrice.toString(),
            priceId: (+price.id),
            srcset: track.srcset,
            variant: price.name,
            category: Track.GetFirstCategoryName(track),
            isSubscription: false,
          };
        this.add(item);
        subscriber.next();
      }
    }).pipe(first());
  }

  /**
   * Create a new cart item. Called from track details when a subscription is added to the cart.
   */
  addSubscription(subscription: SubscriptionDetail, monthly: boolean = null, fromCart = false): Observable<void> {
    if (this.hasSameSubscription(subscription, monthly)) {
      return of<void>(null);
    }
    return new Observable<void>((subscriber) => {
      if (this.hasTrack()) {
        this.openMixedCartSubscriptionModal(subscription, monthly, subscriber);
      } else {
        const item: CartItem = {
          name: subscription.name,
          id: subscription.id,
          price: subscription.price.toString(),
          shortName: subscription.shortName,
          isSubscription: true,
          isMonthly: monthly,
        };
        this.add(item, fromCart);
        subscriber.next();
      }
    }).pipe(first());
  }

  /**
   * Remove item from the cart and stores the new cart in localStorage
   * to keep the cart available in between page reloads.
   */
  remove(item: CartItem) {
    const index = this.cart.value.indexOf(item, 0);
    if (index > -1) {
      // splice returns an array with the deleted item
      // and deletes the item from the original array
      this.cart.value.splice(index, 1);
      this.cart.next(this.cart.value);
    }
    localStorage.setItem('cart', JSON.stringify(this.cart.value));
    this.gtag.removeItemFromCart(item);
  }

  /**
   * List all the cart items.
   */
  getAll(): Array<CartItem> {
    return this.cart.value;
  }

  /**
   * Returns true if there is a sponsorship program in the cart.
   */
  hasSponsorshipProgram(): boolean {
    for (const item of this.cart.value) {
      if (item.shortName?.toLowerCase()?.trim() === 'sponsor') {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true if there are subscription items in the cart.
   */
  hasSubscription(): boolean {
    return this.cart.value.find(i => i.isSubscription) != null;
  }

  hasSameSubscription(subscription: SubscriptionDetail, isMonthly: boolean): boolean {
    return this.cart.value.find(i => i.id === subscription.id && i.isMonthly === isMonthly) !== undefined;
  }

  /**
   * Returns true if there are subscriptions in the cart.
   */
  hasTrack(): boolean {
    return this.cart.value.find(i => !i.isSubscription) != null;
  }

  removeSubscriptions() {
    const nonSubscriptions = this.cart.value.filter(i => !i.isSubscription);
    this.setCart(nonSubscriptions);
  }

  removeTracks() {
    const nonTracks = this.cart.value.filter(i => i.isSubscription);
    this.setCart(nonTracks);
  }

  /**
   * Delete all items from cart.
   */
  clear() {
    this.cart.next([]);
    localStorage.removeItem('cart');
  }

  /**
   * Saves a flag indicating the cart should be cleared.
   */
  setShouldClear() {
    localStorage.setItem('clear-cart', 'true');
  }

  /**
   * Clears the cart if the "should clear" flag is set.
   */
  maybeClear() {
    if (localStorage.getItem('clear-cart')) {
      localStorage.removeItem('clear-cart');
      localStorage.removeItem('cart');
    }
  }

  /**
   * Sends all the data to the payment service when the user is ready to buy.
   */
  buy(data: ProcessRequest): Observable<ProcessResponse> {
    data.cart = this.cart.value;
    data.referral = this.referral.get();
    return this.http.post<ProcessResponse>(this.url + '/snack/v1/payments/process', data).pipe(
      map((response) => {
          // Clear cart the next time the site is loaded
          this.setShouldClear();
          return response;
        },
      ));
  }

  getPaddlePayLink(data: ProcessRequest): Observable<PaddleInitResponse> {
    data.cart = this.cart.value;
    data.referral = this.referral.get();
    return this.http.post<PaddleInitResponse>(this.url + '/snack/v1/payments/checkout-init', data);
  }

  sendError(event: any): Observable<void> {
    if (!event?.eventData?.error || this.lastSentError === event?.eventData?.error) {
      return of<void>(null);
    }
    this.lastSentError = event?.eventData?.error;
    return this.http.post<void>(this.url + '/snack/v1/payments/error', { event });
  }


  /**
   * Sends the current cart and discount code to the discount service.
   */
  applyDiscount(data: ProcessRequest) {
    data.cart = this.cart.value;
    return this.http.post<DiscountResponse>(this.url + '/snack/v1/payments/discount', data);
  }

  get total() {
    let total = 0;
    for (const item of this.cart.value) {
      total = total + (+(item.price));
    }
    return total;
  }

  get isFree() {
    return this.total === 0;
  }


  get totalItems() {
    return this.cart.value.length;
  }

  saveCart(cartItems: CartItem[]): Observable<CartSaved> {
    const data = { cart: cartItems };
    return this.http.post<CartSaved>(this.url + '/snack/v1/cart/save', data);
  }

  getSavedItems(token: string): Observable<RestoreCart> {
    return this.http.get<RestoreCart>(this.url + '/snack/v1/cart/' + token);
  }

  openMixedCartTrackModal(track: Track, price: Price, afterAddToCart: Subscriber<void>) {
    const initialState = {
      isTrack: true,
      doAction: false,
    };
    const modal = this.modalService.show(MixedModalComponent, { initialState, class: 'modal-dialog-centered' });
    modal.onHide.subscribe(() => {
      if (modal.content.doAction) {
        this.removeSubscriptions();
        this.addTrack(track, price).subscribe(() => {
          afterAddToCart.next();
        });
      } else {
        afterAddToCart.next();
      }
    });
  }

  openMixedCartSubscriptionModal(subscription: SubscriptionDetail, monthly: boolean, afterAddToCart: Subscriber<void>) {
    const initialState = {
      isTrack: false,
      doAction: false,
    };
    const modal = this.modalService.show(MixedModalComponent, { initialState, class: 'modal-dialog-centered' });
    modal.onHide.subscribe(() => {
      if (modal.content.doAction) {
        this.removeTracks();
        this.addSubscription(subscription, monthly).subscribe(() => {
          afterAddToCart.next();
        });
      } else {
        afterAddToCart.next();
      }
    });
  }


}
